import { createContext, useContext } from "react";
import { dict, LANGS } from "./dict";
import type { Lang } from "./dict";

export const STORAGE_KEY = "oyufest_lang";
export const DEFAULT_LANG: Lang = "kk";

export type TFn = (key: string, vars?: Record<string, string | number>) => string;

export interface LangContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: TFn;
}

export const LangContext = createContext<LangContextValue | null>(null);

export function isLang(v: unknown): v is Lang {
  return typeof v === "string" && (LANGS as string[]).includes(v);
}

export function readStoredLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLang(stored)) return stored;
  } catch {
    // localStorage may be unavailable (private mode / SSR) — fall back silently.
  }
  return DEFAULT_LANG;
}

/** Replace {placeholders} in a template using the provided vars map. */
export function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in vars ? String(vars[key]) : match,
  );
}

/** Build a translator function bound to a language. */
export function makeT(lang: Lang): TFn {
  return (key, vars) => {
    // Look up dict[lang][key], falling back to ru, then to the key itself.
    const template = dict[lang]?.[key] ?? dict[DEFAULT_LANG]?.[key] ?? key;
    return interpolate(template, vars);
  };
}

export function useT(): LangContextValue {
  const ctx = useContext(LangContext);
  if (!ctx) {
    throw new Error("useT must be used within a LangProvider");
  }
  return ctx;
}
